import * as Sentry from '@sentry/react';
import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { forceRefresh, getMaintenanceContent } from '@marqvision/shared';
import { dismissToast, MDSSnackbar } from '@marqvision/mds-v2';
import { clearAuth, getCachedAuthData, refreshAuth } from '~/apis/auth';
import { HttpClient } from '~/swagger/http-client';
import pkg from '../../package.json';

export const API_ROOT = import.meta.env.REACT_APP_NEW_API_ROOT;
export const OKTA_CLIENT_ID = import.meta.env.REACT_APP_OKTA_CLIENT_ID;
export const OKTA_CALLBACK_URI = encodeURI(import.meta.env.REACT_APP_OKTA_CALLBACK_URI || '');

// 구간별 기준 시간 (ms)
const REQUEST_SEND_THRESHOLD_MS = 200; // 요청 보내기 지연 (네트워크 대기 포함)
const SERVER_RESPONSE_THRESHOLD_MS = 500; // 서버 응답 지연 (API 서버 처리 시간)
const DOWNLOAD_THRESHOLD_MS = 3000; // 다운로드 지연

const axiosConfig = {
  baseURL: API_ROOT,
  headers: {
    'X-Client-Version': pkg.version,
  },
};

function reportToSentry(message: any, isMajorError = false, ignoreDefaultErrorToast = false) {
  captureException(message, isMajorError);
  dismissToast();
  if (!ignoreDefaultErrorToast) {
    MDSSnackbar({
      type: 'error',
      title: 'error',
      message: message,
    });
  }
}

function logPerformanceMetrics(config: any) {
  // Resource Timing API로 axios 요청 관련 성능 정보 가져오기
  const resourceEntries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const matchedResource = resourceEntries.find((entry) => entry.name.includes(config.url));

  let requestDuration = 0;
  let serverProcessingDuration = 0;
  let downloadDuration = 0;

  if (matchedResource) {
    // 각각 구간별 시간 계산
    requestDuration = matchedResource.requestStart ? matchedResource.requestStart - matchedResource.startTime : 0;
    serverProcessingDuration = matchedResource.responseStart
      ? matchedResource.requestStart
        ? matchedResource.responseStart - matchedResource.requestStart
        : matchedResource.responseStart - matchedResource.startTime
      : 0;
    downloadDuration = matchedResource.responseStart
      ? matchedResource.responseEnd - matchedResource.responseStart
      : matchedResource.responseEnd - matchedResource.startTime;
  }

  // 요청 보내기 지연 체크
  if (requestDuration > REQUEST_SEND_THRESHOLD_MS) {
    Sentry.captureMessage(
      `[Slow Request Sending] ${config.method?.toUpperCase()} ${config.url} - ${requestDuration}ms`,
      {
        level: 'error',
        tags: {
          slow_request: 'true',
          phase: 'request_send',
          url: config.url || '',
          method: config.method || '',
        },
        extra: { matchedResource, requestDuration },
      }
    );
  }

  // 서버 응답 지연 체크
  if (serverProcessingDuration > SERVER_RESPONSE_THRESHOLD_MS) {
    Sentry.captureMessage(
      `[Slow Server Response] ${config.method?.toUpperCase()} ${config.url} - ${serverProcessingDuration}ms`,
      {
        level: 'warning',
        tags: {
          slow_request: 'true',
          phase: 'server_response',
          url: config.url || '',
          method: config.method || '',
        },
        extra: { matchedResource, serverProcessingDuration },
      }
    );
  }

  // 다운로드 지연 체크
  if (downloadDuration > DOWNLOAD_THRESHOLD_MS) {
    Sentry.captureMessage(
      `[Slow Content Download] ${config.method?.toUpperCase()} ${config.url} - ${downloadDuration}ms`,
      {
        level: 'info',
        tags: {
          slow_request: 'true',
          phase: 'download',
          url: config.url || '',
          method: config.method || '',
        },
        extra: { matchedResource, downloadDuration },
      }
    );
  }
}

export const http: HttpClient = new HttpClient(axiosConfig);

export function captureException(message?: string, isMajorError = false) {
  if (isMajorError) {
    Sentry.captureException(message);
    return;
  }

  Sentry.configureScope(function (scope) {
    scope.setLevel('warning');
    Sentry.captureException(new Error(message));
  });
}

(function initializeToErrorHandling() {
  const requestWaitQueue: ((token: string) => void)[] = [];
  const messageQueue = new Set<string>();
  let isInProgressRefreshToken = false;

  async function updateToken() {
    isInProgressRefreshToken = true;

    const data = getCachedAuthData();

    try {
      if (data?.refresh_token) {
        return await refreshAuth(data.refresh_token);
      } else {
        throw new Error('refresh token is not exist.');
      }
    } catch (e) {
      clearAuth(true);
    } finally {
      isInProgressRefreshToken = false;
    }
  }

  const responseInterceptorFulfilled = (response: any) => {
    logPerformanceMetrics(response.config);
    messageQueue.clear();
    return response;
  };

  const responseInterceptorRejected = async (error?: any) => {
    logPerformanceMetrics(error.config);
    if (error.name === 'CanceledError') {
      // 의도적으로 취소한 요청은 에러 처리를 하지 않는다
      return;
    }
    if (!error || !error.response) {
      reportToSentry(error.message, true, messageQueue.has(error.message));
      messageQueue.add(error.message);
      return Promise.reject(error);
    }

    if (error.config?.url.indexOf('/refresh_token') >= 0) {
      clearAuth(true);
      return;
    }

    const status = error.response.status;
    const message =
      error.response.data && error.response.data?.display_message
        ? error.response.data?.display_message
        : 'An error occurred, Please try again later.';

    if (error.config && status === 401) {
      const { config: requestConfigToRetry } = error;

      if (!isInProgressRefreshToken) {
        updateToken().then((accessToken) => {
          while (accessToken && requestWaitQueue.length > 0) {
            const waitingRequest = requestWaitQueue.shift();
            waitingRequest?.(accessToken);
          }
        });
      }

      return new Promise((resolve) => {
        requestWaitQueue.push((newAccessToken: string) => {
          requestConfigToRetry['headers'] = {
            ...requestConfigToRetry.headers,
            Authorization: `Bearer ${newAccessToken}`,
          };

          resolve(axios.request(requestConfigToRetry));
        });
      });
    } else if (status === 503) {
      // Service Unavailable
      document.documentElement.innerHTML = await getMaintenanceContent();
    } else if (status === 426) {
      // Upgrade Required
      forceRefresh();
    } else if (status >= 500 || status === 422) {
      // Major Error
      reportToSentry(message, true);
    } else {
      // >>>> PROD-8076 [jamie] 구현되면 아래 438 조건문 제거 - 바로 reportToSentry만 호출.
      if (
        status === 438 &&
        (error.config?.url.indexOf('/default_card/add') >= 0 || error.config?.url.indexOf('/default_card/update') >= 0)
      ) {
        reportToSentry(message, false, true);
      } else if (['/login'].includes(error.config?.url)) {
        // todo-@elie: base.ts 에러 핸들링 수정 필요 - 부분집합만 토스트를 노출하고 나머지는 뷰에서 처리하는 방향에 대해 싱크 필요.
        reportToSentry(message, false, true);
      } else {
        reportToSentry(message);
      }
    }

    // TODO: [PROD-8076] [jamie] raw axios object를 사용하는 곳을 점검한 뒤 Promise.resolve(error)로 넘기기
    return Promise.reject(error);
  };

  axios.interceptors.response.use(responseInterceptorFulfilled, responseInterceptorRejected);

  http.instance.interceptors.response.use(responseInterceptorFulfilled, responseInterceptorRejected);
})();

export function get<T>(url: string, params?: T, auth = true) {
  const config: AxiosRequestConfig = {
    baseURL: API_ROOT,
    params: params,
  };

  const authToken = getCachedAuthData()?.access_token;

  if (auth && authToken) {
    config.headers = { Authorization: 'Bearer ' + authToken };
  }

  return axios.get(url, config);
}

export function post<T>(url: string, data: T, auth = true, isResponseTypeBlob?: boolean) {
  const config: AxiosRequestConfig = {
    baseURL: API_ROOT,
  };

  const authToken = getCachedAuthData()?.access_token;

  if (auth && authToken) {
    config.headers = { Authorization: 'Bearer ' + authToken };
  }

  if (isResponseTypeBlob) {
    config.responseType = 'blob';
  }

  return axios.post(url, data, config);
}

http.instance.interceptors.request.use((config) => {
  const newConfig = {
    ...axiosConfig,
    ...config,
  };

  const authToken = getCachedAuthData()?.access_token;
  if (authToken) {
    if (newConfig.headers) {
      newConfig.headers.Authorization = `Bearer ${authToken}`;
    } else {
      newConfig.headers = { Authorization: `Bearer ${authToken}` } as AxiosRequestHeaders;
    }
  }

  return newConfig;
});
